import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import Button from "@mui/material/Button";
import { FileUpload } from "../../Shared/FileUpload";
import MDialog from "components/Shared/MDialog.tsx";
import MGridForm from "components/Shared/MGridForm";
import { DATA_TYPE_STRING, DATA_TYPE_MULTI_LINE_STRING, DATA_TYPE_CHECKBOX, DATA_TYPE_DROPDOWN2 } from "typeConstants/dataTypes";
import validator from "components/Shared/MGridFormDefinitions/validator";
import responseHandler from "responseHandler";
import { startEmailTransaction, uploadAttachment } from "requests/homeowner/caseEmail";
import { MTable } from "components/Shared";
import { PhotoTool } from "components/Photos";
import { CHANGE_SENDER_EMAIL } from "typeConstants/customerFeatures";
import { CUSTOMER_SERVICE_EMAIL_SEND } from 'typeConstants/rights';
import { userHasRight } from "selectors/rights";

// https://stackoverflow.com/questions/16245767/creating-a-blob-from-a-base64-string-in-javascript
function base64toBlob(base64Data, contentType) {
  contentType = contentType || "";
  const sliceSize = 1024;
  const byteCharacters = atob(base64Data);
  const bytesLength = byteCharacters.length;
  const slicesCount = Math.ceil(bytesLength / sliceSize);
  const byteArrays = new Array(slicesCount);

  for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
    const begin = sliceIndex * sliceSize;
    const end = Math.min(begin + sliceSize, bytesLength);

    const bytes = new Array(end - begin);
    for (let offset = begin, i = 0; offset < end; ++i, ++offset) {
      bytes[i] = byteCharacters[offset].charCodeAt(0);
    }
    byteArrays[sliceIndex] = new Uint8Array(bytes);
  }
  return new Blob(byteArrays, { type: contentType });
}

/* eslint-disable react/display-name */

const getDeleteButton = (callback) => {
  return rowData => {
    return (
      <div className="cs-table-action-container">
        <i className="md-icon material-icons" onClick={() => callback(rowData)}>delete</i>
      </div>
    );
  };
};

/* eslint-enable react/display-name */

const CaseEmail = ({
  controlNumber,
  sendToReadOnly,
  makeToast,
  userEmail,
  sendToEmail,
  subject,
  sendMethod,
  caseFileAttachments,
  sentCallback,
  customFields,
  enabledFeatures,
  canEditEmailFrom,
  canEmailCustomerService,
}) => {

  const emptyEmailContent = {
    controlNumber: controlNumber,
    from: "",
    fromOverride: "",
    to: [],
    toSingleLine: sendToEmail ? sendToEmail + ";" : "",
    cc: [],
    ccSingleLine: userEmail + ";",
    subject: subject ? subject : "",
    note: "",
    sending: false,
  };

  const [emailTransactionId, setEmailTransactionId] = useState("");
  const [caseAttachments, setCaseAttachments] = useState([]);
  const [userAttachments, setUserAttachments] = useState([]);
  const [emailContent, setEmailContent] = useState(emptyEmailContent);
  const [warningDialogVisible, setWarningDialogVisible] = useState(false);
  const emailControlMethods = useRef({});
  const attachmentsControlMethods = useRef({});
  const photoControlMethods = useRef({});
  const mounted = useRef(true);
  const attachmentsFormDefinition = useRef([]);
  const [isCustomFromAddress, setIsCustomFromAddress] = useState(false);
  const newEmailAddressValue = "new";

  useEffect(() => {
    startTransaction();
    return (() => { mounted.current = false; });
  }, []);

  useEffect(() => {
    if (emailContent.sending == false) return;
    userAttachments.reduce((acc, file) => () => uploadAttachment(emailTransactionId, file, acc, mounted), () => sendMethod(emailTransactionId, emailContent, onSuccess, reset, mounted))();
  }, [emailContent.sending]);

  useEffect(() => {
    handleCaseAttachments();
    setEmailContent({ ...emptyEmailContent });
  }, [sendToEmail, caseFileAttachments]);

  const setId = (response) => setEmailTransactionId(response.transactionId);
  const startTransaction = () => startEmailTransaction(controlNumber, setId, mounted);

  const handleCaseAttachments = () => {
    if (caseFileAttachments && caseFileAttachments.length) {
      const attachmentsObj = {};
      attachmentsFormDefinition.current = [];
      caseFileAttachments.forEach((file, i) => {
        const propName = `attachment${i}`;
        attachmentsObj[propName] = { fileName: file.fileName, include: false };
        attachmentsFormDefinition.current.push(
          [
            { dataType: DATA_TYPE_CHECKBOX, label: file.fileName, source: `${propName}.include` }
          ]
        );
      });
      setEmailContent({ ...emptyEmailContent });
      setCaseAttachments(attachmentsObj);
    }
  };

  const getEmailFormDefinition = () => {
    let customEmailOptions = [];
    let customFromAddress = "";
    let internalEmailOptions = [
      { label: "customerservice@muellerreports.com", value: "customerservice@muellerreports.com" }, 
      { label: "ValuationCS@muellerreports.com", value: "ValuationCS@muellerreports.com" },       
      { label: "NEW", value: newEmailAddressValue }
    ];
    let defaultEmailOptions = [ { label: userEmail, value: userEmail } ];

    if(canEmailCustomerService){
      let customSenderFeature = enabledFeatures
        ?.find(s => s.feature === CHANGE_SENDER_EMAIL);
      if(customSenderFeature){
        customEmailOptions = [{ label: userEmail, value: userEmail }];
        customFromAddress = customSenderFeature.featureSettings?.find(p => p.code === "CUSTOM_EMAIL_SENDER_ADDRESS")?.value;
        if(customFromAddress){
          customEmailOptions.unshift({ label: customFromAddress, value: customFromAddress });      
        }      
      }
    }

    let def = [
      [
        customEmailOptions.length ?
        { dataType: DATA_TYPE_DROPDOWN2, label: "From:", value: customFromAddress, dataTypeOptions: { options: customEmailOptions }, source: "from" }
        : canEditEmailFrom 
          ? { dataType: DATA_TYPE_DROPDOWN2, label: "From:", value: "customerservice@muellerreports.com", dataTypeOptions: { options: internalEmailOptions }, source: "from" }
          : { dataType: DATA_TYPE_DROPDOWN2, label: "From:", value: userEmail, dataTypeOptions: { options: defaultEmailOptions }, source: "from" }
      ]
    ];
    if(isCustomFromAddress){
      def.push([{ dataType: DATA_TYPE_STRING, label: "From:", source: "fromOverride" }]);
    }      
    def.push([{ dataType: DATA_TYPE_STRING, label: "To:", source: "toSingleLine", hasValidationError: validator.semiColonSeparatedEmailAddressesRequired, readOnly: false, enabled: () => !sendToReadOnly }]);
    def.push([{ dataType: DATA_TYPE_STRING, label: "CC:", source: "ccSingleLine", hasValidationError: validator.semiColonSeparatedEmailAddresses }]);
    def.push([{ dataType: DATA_TYPE_STRING, label: "Subject:", source: "subject", hasValidationError: validator.required }]);
    if (customFields && Array.isArray(customFields)) {
      if (Array.isArray(customFields[0])) { // An array of arrays, contains multiple fields
        def = def.concat(customFields);
      } else {
        def.push(customFields); // An array, probably contains a single field
      }
    }
    def.push([{ dataType: DATA_TYPE_MULTI_LINE_STRING, label: "Note:", source: "note" }]);
    return def;
  };

  const handleEmailChange = (updatedObject) => {
    if(updatedObject.from === newEmailAddressValue && !isCustomFromAddress){
      setIsCustomFromAddress(true);
    } else if (updatedObject.from !== newEmailAddressValue && isCustomFromAddress){
      updatedObject.fromOverride = "";
      setIsCustomFromAddress(false);      
    }
    setEmailContent({ ...updatedObject });
  };
  const handleCaseAttachmentsChange = (updatedObject) => setCaseAttachments({ ...updatedObject });
  const handleAttachmentLoadStart = (file) => {
    if (userAttachments.some(a => a.name === file.name)) {
      setWarningDialogVisible(true);
      return;
    }

    setUserAttachments([...userAttachments, file]);
  };
  const handleUserAttachmentLoad = (file, uploadResult) => {
    if (userAttachments.some(a => a.name === file.name)) return;

    const { name, size, type } = file;

    // FileUpload returns the base64 data prefixed with content type and ";Base64,"
    const base64Data = uploadResult.substring(uploadResult.indexOf(",") + 1);
    const contentType = uploadResult.substring(0, uploadResult.indexOf(";"));

    const attachment = {
      name,
      type,
      size,
      uploadResult: base64toBlob(base64Data, contentType)
    };
    setUserAttachments([...userAttachments.filter(a => a.name !== name), attachment]);
  };

  const deleteAttachment = (rowData) => {
    setUserAttachments([...userAttachments.filter(a => a.name !== rowData.name)]);
  };

  const getColumnDefs = () => {
    return [
      { name: "My Attachments", dataSource: "name" },
      { name: "Delete", dataSource: getDeleteButton(deleteAttachment) }
    ];
  };

  const onSubmit = () => {
    const toEmails = emailContent.toSingleLine.split(";").filter(Boolean);
    const ccEmails = emailContent.ccSingleLine != "" ? emailContent.ccSingleLine.split(";").filter(Boolean) : null;
    const fromEmail = emailContent.fromOverride || emailContent.from;
    const attachmentFileNames = [];
    Object.keys(caseAttachments).forEach(key => {
      if (typeof caseAttachments[key] === "object" && Object.keys(caseAttachments[key]).includes("include")) {
        if (caseAttachments[key].include) {
          attachmentFileNames.push(caseAttachments[key].fileName);
        }
      }
    });
    const photoIds = photoControlMethods.current.getselectedPhotoIds();
    setEmailContent({ ...emailContent, to: toEmails, from: fromEmail, cc: ccEmails, attachments: attachmentFileNames, photos: photoIds, sending: true });
  };

  const reset = () => {
    setEmailContent({ ...emptyEmailContent });
    const attachmentsObj = { ...caseAttachments };
    Object.keys(attachmentsObj).forEach(key => attachmentsObj[key].include = false);
    setCaseAttachments({ ...attachmentsObj });
  };

  const onSuccess = () => {
    makeToast("Email Sent", "success");
    sentCallback && sentCallback();
  };

  const dialogActions = [
    <Button key="cancel" variant="text" color="secondary" onClick={() => setWarningDialogVisible(false)}>Cancel</Button>
  ];

  return (
    <>
      <div className="case-email-container mgridform-flat">
        <MGridForm
          key={"email-form"}
          data={emailContent}
          onChange={handleEmailChange}
          formDefinition={getEmailFormDefinition()}
          getControlMethods={cm => { emailControlMethods.current = cm; }}
          webform={true}
        />
        <p>Attach Case Photos</p>
        <PhotoTool
          controlNumber={controlNumber}
          getControlMethods={cm => { photoControlMethods.current = cm; }}
          multiSelect
          customerSendEmailView
        />
        {!!attachmentsFormDefinition.current.length && <>
          <p>Attach Case Files</p>
          <MGridForm
            key={"attachments-forms"}
            data={caseAttachments}
            onChange={handleCaseAttachmentsChange}
            formDefinition={attachmentsFormDefinition.current}
            getControlMethods={cm => { attachmentsControlMethods.current = cm; }}
            webform={true}
          />
        </>}
        <div className="user-uploads-container">
          {!!userAttachments.length &&
            <MTable
              keyProperty="file"
              columnDefinitions={getColumnDefs()}
              data={userAttachments.map(a => ({ key: a.name, name: a.name }))}
            />
          }
          <FileUpload
            accept=".pdf"
            allowDuplicates
            id="importFileInput"
            label={"Upload Attachment"}
            onLoadStart={handleAttachmentLoadStart}
            onLoad={handleUserAttachmentLoad}
            className="file-upload-button"
            name="file"
            primary
            iconBefore={false}
          />
        </div>
        <div className="send-button-container">
          <Button
            key="send-button"
            onClick={() => { if (emailControlMethods.current.isFormValid()) onSubmit(); }}
            disabled={emailContent.sending}>
            Send
      </Button>
        </div>
      </div>
      <MDialog
        id="email-warning-dialog"
        title="Warning"
        visible={warningDialogVisible}
        contentActions={dialogActions}
        focusOnMount={false}
        width={320}
      >
        Attachments cannot have the same file name.
    </MDialog>
    </>
  );
};

CaseEmail.propTypes = {
  controlNumber: PropTypes.number.isRequired, // Control number for the case to send an email about
  sendMethod: PropTypes.func.isRequired,  // Function to call when sending email, will be called with (emailTransactionId, payload, successCallback, mountedRef)
  sendToEmail: PropTypes.string,  // Email to prepopulate the send to field
  sendToReadOnly: PropTypes.bool, // Allow the user to enter or edit emails to send to. Set to true to prevent user from editing send to field
  makeToast: PropTypes.func.isRequired, // From dispatch
  customFields: PropTypes.oneOfType([ // Pass in custom MGridForm definitions that will be included in the form, see structure above
    PropTypes.array,
    PropTypes.arrayOf(PropTypes.array)
  ]),
  userEmail: PropTypes.string,  // The email of the user, used to populate the CC email field with their email
  subject: PropTypes.string,  // String to use for the subject, user can edit
  caseFileAttachments: PropTypes.array, /*
    An array of case files that could be included as attachments to the email.
    Expected to be the array of attached files from the getAttachments request
    as we need to check the type of attachment before displaying it
  */
  sentCallback: PropTypes.func, // Optional function to be called after sending email
  enabledFeatures: PropTypes.array,
  canEditEmailFrom: PropTypes.bool,
  canEmailCustomerService: PropTypes.bool,
};

const mapStateToProps = state => {
  return {
    userEmail: state.user.profile.email,
    canEmailCustomerService: userHasRight(CUSTOMER_SERVICE_EMAIL_SEND)(state),
  };
};

const mapDispatchToProps = dispatch => ({
  makeToast(text, type) {
    responseHandler.customToast(text, type, dispatch);
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(CaseEmail);
